import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Container,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Work as WorkIcon,
  LocalOffer as CouponIcon,
  VerifiedUser as ServicesIcon,
  LocalLibrary as CoursesIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  OpenInNew as OpenInNewIcon,
  Gavel as DisclaimerIcon,
  Person as PersonIcon,
  PictureAsPdf as ResumeIcon,
  CloudDownload as CloudDownloadIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

// Use env var if set, otherwise use relative URL (works for same-domain backend)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let base = process.env.REACT_APP_API_URL;
    // Remove trailing slash
    base = base.endsWith('/') ? base.slice(0, -1) : base;
    // Remove /api suffix if present (we'll add it back)
    if (base.endsWith('/api')) {
      base = base.slice(0, -4);
    }
    return base;
  }
  // In production (ondigitalocean.app or services.knls.in), use relative URL
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return ''; // Empty string means relative URL - will become /api/... when concatenated
  }
  // Development fallback
  return 'http://localhost:3000';
};

const UserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);

  useEffect(() => {
    document.title = 'LOCSY User Dashboard';
    return () => {
      document.title = 'LOCSY Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (user?.phone || user?.phone_number) {
      fetchAppliedJobs();
    }
    fetchVisibleCards();
  }, [user]);

  // Refresh visible cards when component mounts or when returning to this page
  useEffect(() => {
    const handleFocus = () => {
      fetchVisibleCards();
    };
    window.addEventListener('focus', handleFocus);
    // Also refresh when page becomes visible (handles tab switching)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        fetchVisibleCards();
      }
    });
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, []);

  const fetchVisibleCards = async () => {
    setLoadingCards(true);
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.get(`${baseUrl}/api/dashboard-cards/visible`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        setVisibleCards(response.data.data);
      } else {
        // If API returns empty or no data, use default cards as fallback
        console.warn('No visible cards returned from API, using default cards');
        setVisibleCards([
          { key: 'coupons', displayOrder: 1 },
          { key: 'job_alerts', displayOrder: 2 },
          { key: 'application_chat', displayOrder: 2.2 },
          { key: 'career_profile', displayOrder: 3 },
          { key: 'resume_generator', displayOrder: 4 },
          { key: 'services', displayOrder: 5 },
          { key: 'courses', displayOrder: 6 },
        ]);
      }
    } catch (err) {
      console.error('Error fetching visible cards:', err);
      // On error, show default cards as fallback (better UX than showing nothing)
      console.warn('Using default cards due to API error');
      setVisibleCards([
        { key: 'coupons', displayOrder: 1 },
        { key: 'job_alerts', displayOrder: 2 },
        { key: 'application_chat', displayOrder: 2.2 },
        { key: 'career_profile', displayOrder: 3 },
        { key: 'resume_generator', displayOrder: 4 },
        { key: 'services', displayOrder: 5 },
        { key: 'courses', displayOrder: 6 },
      ]);
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchAppliedJobs = async () => {
    const phone = user?.phone || user?.phone_number;
    if (!phone) return;

    setLoadingJobs(true);
    setJobsError(null);

    try {
      const cleanPhone = phone.replace(/\D/g, '').slice(0, 10);
      const baseUrl = getApiBaseUrl();
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(
        `${baseUrl}/api/user-applications/user/${cleanPhone}`,
        { headers }
      );

      if (response.data.success) {
        const apps = response.data.data || [];
        setAppliedJobs(
          apps.map((app) => ({
            id: app.id,
            notificationName:
              app.notificationName ||
              app.jobAlert?.notificationName ||
              'Applied Job',
            organization:
              app.organization || app.jobAlert?.organization || '',
            qualification:
              app.qualification || app.jobAlert?.qualification || '',
            numberOfVacancies:
              app.numberOfVacancies ??
              app.jobAlert?.numberOfVacancies ??
              0,
            lastDate: app.lastDate || app.jobAlert?.lastDate || null,
            applicationStatus: app.applicationStatus || 'applied',
            billNumber: app.billNumber || null,
            appliedAt: app.createdAt || null,
          }))
        );
      } else {
        setAppliedJobs([]);
      }
    } catch (err) {
      console.error('Error fetching applied jobs:', err);
      setJobsError('Failed to load applied jobs. Please try again.');
      setAppliedJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Card definitions with mapping to card keys
  const allDashboardCards = {
    job_alerts: {
      title: 'Job Alerts',
      description: 'Browse and apply for job opportunities. Find your dream job from thousands of openings.',
      icon: <WorkIcon />,
      color: '#1976d2',
      path: '/user/job-alerts',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)', // Blue gradient
      bodyGradient: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(25, 118, 210, 0.08) 100%)',
    },
    job_alerts_registration: {
      title: 'Job Alerts Registration',
      description: 'Register for job alerts by filling out your details. Get notified about relevant job opportunities.',
      icon: <WorkIcon />,
      color: '#388e3c',
      path: '/user/job-alerts-registration',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)', // Green gradient
      bodyGradient: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(56, 142, 60, 0.08) 100%)',
    },
    career_profile: {
      title: 'Career Profile',
      description: 'Complete your career profile to get matched with relevant job opportunities automatically.',
      icon: <PersonIcon />,
      color: '#7B1FA2',
      path: '/user/career-profile',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)', // Purple gradient (different from job alerts)
      bodyGradient: 'linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(123, 31, 162, 0.08) 100%)',
    },
    resume_generator: {
      title: 'Resume Generator',
      description: 'Generate professional resumes in PDF format from your career profile.',
      icon: <ResumeIcon />,
      color: '#8B4513',
      path: '/user/resume',
      gradient: 'linear-gradient(135deg, #A0522D 0%, #8B4513 100%)', // Rustic brown/sienna gradient
      bodyGradient: 'linear-gradient(135deg, rgba(160, 82, 45, 0.08) 0%, rgba(139, 69, 19, 0.08) 100%)',
    },
    // pdf_download: {
    //   title: 'New PDF',
    //   description: 'Download your career profile as a PDF file directly from the server.',
    //   icon: <CloudDownloadIcon />,
    //   color: '#D32F2F',
    //   path: '/user/pdfresume',
    //   gradient: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)', // Red gradient
    //   bodyGradient: 'linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(211, 47, 47, 0.08) 100%)',
    // }, // Hidden - page removed
    coupons: {
      title: 'Coupons',
      description: 'Discover amazing discounts and offers. Save money with exclusive coupons.',
      icon: <CouponIcon />,
      color: '#F57C00',
      path: '/user/coupons',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', // Orange gradient (distinct)
      bodyGradient: 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(245, 124, 0, 0.08) 100%)',
    },
    application_chat: {
      title: 'Application Chat',
      description: 'Review application previews and confirm or request edits in chat.',
      icon: <ForumIcon />,
      color: '#0f766e',
      path: '/user/confirmations',
      gradient: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
      bodyGradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.08) 0%, rgba(15, 118, 110, 0.08) 100%)',
    },
    services: {
      title: 'Services',
      description: 'Access all government and civic services. Aadhar, PAN, Passport, and more.',
      icon: <ServicesIcon />,
      color: '#00796B',
      path: '/services',
      gradient: 'linear-gradient(135deg, #009688 0%, #00796B 100%)', // Teal/Green gradient
      bodyGradient: 'linear-gradient(135deg, rgba(0, 150, 136, 0.08) 0%, rgba(0, 121, 107, 0.08) 100%)',
    },
    courses: {
      title: 'KNLS Computer Courses',
      description: 'Enhance your skills with comprehensive courses. IT, Languages, and Professional Development.',
      icon: <CoursesIcon />,
      color: '#6B4423',
      path: '/courses',
      gradient: 'linear-gradient(135deg, #8B6F47 0%, #6B4423 100%)', // Rustic terracotta/earth tone gradient
      bodyGradient: 'linear-gradient(135deg, rgba(139, 111, 71, 0.08) 0%, rgba(107, 68, 35, 0.08) 100%)',
    },
  };

  // Filter and sort cards based on visibility settings
  // Always include required user cards (even if API doesn't return them)
  const requiredUserCards = {
    'coupons': 1,
    'job_alerts': 2,
    'application_chat': 2.2,
    'job_alerts_registration': 2.5, // Between job_alerts and career_profile
    'career_profile': 3,
    'resume_generator': 4,
    // 'pdf_download': 5 // Hidden - page removed
  };
  
  // Merge API visible cards with required user cards
  const allCardKeys = new Set([
    ...visibleCards.map(c => c.key),
    ...Object.keys(requiredUserCards)
  ]);
  
  const dashboardCards = Array.from(allCardKeys)
    .map((cardKey) => {
      const cardDef = allDashboardCards[cardKey];
      if (!cardDef) return null;
      const visibleCard = visibleCards.find(c => c.key === cardKey);
      // Use API order if available, otherwise use requiredUserCards order, otherwise 999
      const displayOrder = visibleCard?.displayOrder || requiredUserCards[cardKey] || 999;
      return {
        ...cardDef,
        key: cardKey,
        displayOrder: displayOrder,
      };
    })
    .filter((card) => card && card.title) // Only include cards that exist in our definitions
    .sort((a, b) => {
      return a.displayOrder - b.displayOrder;
    });
  
  // Separate cards into main cards and KNLS Services/Courses
  const mainCards = dashboardCards.filter(card => card.key !== 'services' && card.key !== 'courses');
  const knlsCards = dashboardCards.filter(card => card.key === 'services' || card.key === 'courses');

  // Show cards when loaded, or empty array while loading
  const displayMainCards = loadingCards ? [] : mainCards;
  const displayKnlsCards = loadingCards ? [] : knlsCards;

  // Always ensure KNLS cards are available even if not from API
  const knlsServicesCard = allDashboardCards.services;
  const knlsCoursesCard = allDashboardCards.courses;

  // Check if cards exist before rendering
  if (!knlsServicesCard || !knlsCoursesCard) {
    console.error('KNLS cards are not defined');
  }

  // Debug logging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('Dashboard Cards Debug:', {
      visibleCardsCount: visibleCards.length,
      visibleCards,
      dashboardCardsCount: dashboardCards.length,
      mainCardsCount: displayMainCards.length,
      knlsCardsCount: displayKnlsCards.length,
      loadingCards,
    });
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Header */}
      <Box
        sx={{
          background: 'linear-gradient(120deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          p: 4,
          mb: 4,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Welcome, {user?.full_name || 'User'}!
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Access job alerts, coupons, services, and courses all in one place
        </Typography>
        <Button
          component="a"
          href="https://loksy.in/citizen/home"
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          startIcon={<OpenInNewIcon />}
          sx={{ mt: 2, bgcolor: '#fff', color: '#3f51b5', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#f4f6ff' } }}
        >
          Open Loksy Citizen App
        </Button>
      </Box>

      {/* Main Dashboard Cards Section */}
      <Grid container spacing={3}>
        {displayMainCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                  borderColor: card.color,
                },
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(card.path);
              }}
            >
              <Box
                sx={{
                  background: card.gradient,
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {card.icon}
                </Avatar>
              </Box>
              <CardContent 
                sx={{ 
                  p: 3,
                  background: card.bodyGradient || 'transparent',
                }}
              >
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
                  {card.description}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: card.color,
                    '&:hover': {
                      bgcolor: card.color,
                      opacity: 0.9,
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(card.path);
                  }}
                >
                  Explore {card.title}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* KNLS Online Services & Training Section */}
      {knlsServicesCard && knlsCoursesCard && (
        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            sx={{
              mb: 3,
              textAlign: 'center',
              color: 'text.primary',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 80,
                height: 4,
                background: 'linear-gradient(90deg, #667eea, #764ba2)',
                borderRadius: 2,
              },
            }}
          >
            KNLS Online Services & Training
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                    borderColor: knlsServicesCard.color,
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (knlsServicesCard?.path) {
                    navigate(knlsServicesCard.path, { replace: false });
                  }
                }}
              >
              <Box
                sx={{
                  background: knlsServicesCard.gradient,
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {knlsServicesCard.icon}
                </Avatar>
              </Box>
              <CardContent 
                sx={{ 
                  p: 3,
                  background: knlsServicesCard.bodyGradient || 'transparent',
                }}
              >
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {knlsServicesCard.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
                  {knlsServicesCard.description}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: knlsServicesCard.color,
                    '&:hover': {
                      bgcolor: knlsServicesCard.color,
                      opacity: 0.9,
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (knlsServicesCard?.path) {
                      navigate(knlsServicesCard.path, { replace: false });
                    }
                  }}
                >
                  Explore {knlsServicesCard.title}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                  borderColor: knlsCoursesCard.color,
                },
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (knlsCoursesCard?.path) {
                  navigate(knlsCoursesCard.path, { replace: false });
                }
              }}
            >
              <Box
                sx={{
                  background: knlsCoursesCard.gradient,
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {knlsCoursesCard.icon}
                </Avatar>
              </Box>
              <CardContent 
                sx={{ 
                  p: 3,
                  background: knlsCoursesCard.bodyGradient || 'transparent',
                }}
              >
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {knlsCoursesCard.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
                  {knlsCoursesCard.description}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: knlsCoursesCard.color,
                    '&:hover': {
                      bgcolor: knlsCoursesCard.color,
                      opacity: 0.9,
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (knlsCoursesCard?.path) {
                      navigate(knlsCoursesCard.path, { replace: false });
                    }
                  }}
                >
                  Explore {knlsCoursesCard.title}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      )}

      {/* Applied Jobs Section */}
      <Box sx={{ mt: 4 }}>
        <Card
          sx={{
            borderRadius: 3,
            p: 3,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Applied Jobs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {appliedJobs.length > 0
                  ? `You have ${appliedJobs.length} applied job${appliedJobs.length === 1 ? '' : 's'}`
                  : 'Jobs you applied for will appear here'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAppliedJobs}
              disabled={loadingJobs}
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Box>

          {loadingJobs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : jobsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {jobsError}
            </Alert>
          ) : appliedJobs.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                px: 2,
              }}
            >
              <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Applied Jobs Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Applied jobs appear after an Online Application bill is created for your phone number.
              </Typography>
              <Button
                variant="contained"
                startIcon={<WorkIcon />}
                onClick={() => navigate('/user/job-alerts')}
                sx={{ textTransform: 'none' }}
              >
                Browse Job Alerts
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {appliedJobs.slice(0, 3).map((job) => (
                <Grid item xs={12} md={4} key={job.id}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-4px)',
                      },
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate('/user/job-alerts')}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ flex: 1, mr: 1 }}>
                          {job.notificationName || 'Job Alert'}
                        </Typography>
                        {job.isUrgent && (
                          <Chip
                            label="Urgent"
                            color="error"
                            size="small"
                            sx={{ height: 24, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {job.organization || 'N/A'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {job.numberOfVacancies > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {job.numberOfVacancies} Vacancies
                            </Typography>
                          </Box>
                        )}
                        {job.qualification && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SchoolIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {job.qualification}
                            </Typography>
                          </Box>
                        )}
                        {job.lastDate && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Last Date: {new Date(job.lastDate).toLocaleDateString('en-IN')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      {job.onlineApplicationLink && (
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          endIcon={<OpenInNewIcon />}
                          sx={{ mt: 2, textTransform: 'none' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(job.onlineApplicationLink, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          Apply Online
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {appliedJobs.length > 3 && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/user/job-alerts')}
                sx={{ textTransform: 'none' }}
              >
                View All {appliedJobs.length} Job Alerts
              </Button>
            </Box>
          )}
        </Card>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ mt: 4 }}>
        <Card
          sx={{
            borderRadius: 3,
            p: 3,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Quick Access
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use the cards above to navigate to job alerts, coupons, services, and courses. All features are available
            for your convenience.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DisclaimerIcon />}
            endIcon={<OpenInNewIcon />}
            onClick={() => navigate('/disclaimer')}
            sx={{ textTransform: 'none', mt: 1 }}
          >
            View Disclaimer
          </Button>
        </Card>
      </Box>

      {/* Disclaimer Link Footer */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          By using KNLS platform, you agree to our{' '}
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/disclaimer')}
            sx={{
              textTransform: 'none',
              textDecoration: 'underline',
              p: 0,
              minWidth: 'auto',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Terms & Disclaimer
          </Button>
          {' · '}
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/privacy-policy')}
            sx={{
              textTransform: 'none',
              textDecoration: 'underline',
              p: 0,
              minWidth: 'auto',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Privacy Policy
          </Button>
        </Typography>
      </Box>
    </Container>
  );
};

export default UserDashboard;







